/**
 * Enterprise Item Search Modal
 * 
 * Professional item search with grid/list views, filters, and inventory info.
 * Optimized for quick item selection in document entry.
 * 
 * NOW INTEGRATED WITH WAREHOUSE INVENTORY SYSTEM:
 * - Fetches stock levels from v_work_item_stock_by_warehouse view
 * - Shows aggregated stock across all warehouses
 * - Real-time stock availability per warehouse
 * 
 * @version 5.0.0 - Warehouse Integration
 * @date December 2025
 */

'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search, Package, AlertCircle, Grid, List,
  Box, Layers, Plus, Eye, RefreshCw
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useResponsive } from '@/hooks/useResponsive';
import InventoryItemModal, { type InventoryItemData, type InventorySubtype } from '@/components/modals/InventoryItemModal';
import InventoryItemThumbnail from '@/components/inventory/InventoryItemThumbnail';
import { BackorderWorkflow } from '@/components/inventory/BackorderWorkflow';
import type { StockAvailabilityCheck } from '@/lib/inventory/stockReservationService';
import { WideSlideOver, SlideOverHeaderAction } from '@/components/SlideOver';
import { authService } from '@/lib/authService';
import { formatCurrency as formatMoney, formatQuantity } from '@/lib/inventoryFormat';
import { isCapacitor } from '@/lib/platformUtils';
import toast from 'react-hot-toast';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkItemInventory {
  id: string;              // UUID from work_items
  title: string;           // DB column is 'title', not 'name'
  name?: string;           // Alias for backwards compatibility
  description: string | null;
  item_type: string;
  item_subtype: string | null;
  track_inventory?: boolean;   // top-level work_items column — true stock tracking, not the custom_fields copy
  item_number: string | null;  // DB column for friendly code (e.g., "PROD-000006")
  code?: string | null;        // Legacy field, may not exist
  sku: string | null;
  barcode: string | null;
  unit: string | null;
  category: string | null;
  category_id?: number | null;
  contact_id?: number | null;
  contact_name?: string | null;
  primary_image_url?: string | null;
  custom_fields: {
    selling_price?: number;
    cost_price?: number;
    currency?: string;
    tax_rate_id?: number;
    tax_rate_name?: string;
    tax_rate_percentage?: number;
    is_tax_inclusive?: boolean;
    track_inventory?: boolean;
    quantity_on_hand?: number;
    quantity_allocated?: number;
    reorder_level?: number;
    track_serial_numbers?: boolean;
    track_batches?: boolean;
  } | null;
  status: string;
}

// Stock level from warehouse system
export interface WarehouseStockLevel {
  work_item_id: string;
  product_name: string;
  sku: string | null;
  item_number: string | null;
  warehouse_id: number;
  warehouse_name: string;
  warehouse_code: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  reorder_level: number | null;
}

export interface SupplierRelationshipSummary {
  id: number;
  work_item_id: string;
  supplier_contact_id: number;
  supplier_name?: string | null;
  supplier_sku?: string | null;
  cost_price?: number | null;
  currency_code?: string | null;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  is_preferred: boolean;
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
}

export interface PurchaseLineSourcingDetails {
  supplier_mapping_id: number;
  supplier_contact_id: number;
  supplier_name?: string | null;
  supplier_sku?: string | null;
  currency_code?: string | null;
  cost_price?: number | null;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  is_preferred: boolean;
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
  price_source:
    | 'selected_preferred_supplier'
    | 'selected_supplier_mapping'
    | 'preferred_supplier_mapping'
    | 'supplier_mapping';
  matches_document_contact: boolean;
}

export interface FinanceItem {
  id: string;  // UUID
  item_code: string;
  name: string;
  description: string | null;
  item_type: 'product' | 'service' | 'bundle';
  contact_id?: number | null;
  contact_name?: string | null;
  unit_price: number;
  cost_price: number;
  currency: string;
  tax_rate_id: number | null;
  tax_rate_name: string | null;
  tax_rate_percentage: number | undefined;
  is_tax_inclusive: boolean;
  track_inventory: boolean;
  quantity_on_hand: number;
  quantity_allocated: number;
  quantity_available: number;
  reorder_level: number | null;
  track_serial_numbers: boolean;
  track_batches: boolean;
  category_id: number | null;
  category_name: string | null;
  unit_of_measure: string;
  barcode: string | null;
  sku: string | null;
  status: string;
  custom_price?: number;
  primary_image_url?: string | null;
  // Warehouse stock breakdown
  warehouse_stock?: {
    warehouse_id: number;
    warehouse_name: string;
    quantity_on_hand: number;
    quantity_available: number;
  }[];
  // Purchase sourcing (populated for purchase documents)
  purchase_sourcing?: PurchaseLineSourcingDetails;
}

interface WorkItemDetailRecord {
  id: string;
  title?: string | null;
  description?: string | null;
  item_subtype?: string | null;
  status?: string | null;
  sku?: string | null;
  barcode?: string | null;
  category_id?: number | null;
  cost_price?: number | string | null;
  selling_price?: number | string | null;
  currency?: string | null;
  tax_rate_id?: number | null;
  tax_rate?: number | string | null;
  unit_of_measure?: string | null;
  reorder_level?: number | string | null;
  reorder_quantity?: number | string | null;
  is_serialized?: boolean | null;
  is_batch_tracked?: boolean | null;
  has_expiry?: boolean | null;
  manufacturer?: string | null;
  brand?: string | null;
  weight?: number | string | null;
  preferred_supplier_id?: number | null;
  supplier_id?: number | null;
  supplier_sku?: string | null;
  custom_fields?: Record<string, unknown> | string | null;
}

function parseInventoryCustomFields(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function toOptionalNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function mapWorkItemDetailToInventoryItemData(item: WorkItemDetailRecord): InventoryItemData {
  const customFields = parseInventoryCustomFields(item.custom_fields);

  return {
    id: item.id,
    title: item.title || '',
    description: item.description || '',
    item_type: 'inventory',
    item_subtype: (item.item_subtype as InventorySubtype | null) || 'service',
    status: item.status || 'active',
    sku: toOptionalString(item.sku ?? customFields.sku),
    barcode: toOptionalString(item.barcode ?? customFields.barcode),
    category_id: toOptionalNumber(item.category_id ?? customFields.category_id),
    category_name: toOptionalString(customFields.category_name),
    cost_price: toOptionalNumber(item.cost_price ?? customFields.cost_price),
    selling_price: toOptionalNumber(item.selling_price ?? customFields.selling_price),
    currency: toOptionalString(item.currency ?? customFields.currency) || 'ZAR',
    tax_rate_id: toOptionalNumber(item.tax_rate_id ?? customFields.tax_rate_id),
    tax_rate: toOptionalNumber(item.tax_rate ?? customFields.tax_rate),
    unit_of_measure: toOptionalString(item.unit_of_measure ?? customFields.unit_of_measure) || 'each',
    reorder_level: toOptionalNumber(item.reorder_level ?? customFields.reorder_level),
    reorder_quantity: toOptionalNumber(item.reorder_quantity ?? customFields.reorder_quantity),
    is_serialized: Boolean(item.is_serialized ?? customFields.is_serialized),
    is_batch_tracked: Boolean(item.is_batch_tracked ?? customFields.is_batch_tracked),
    has_expiry: Boolean(item.has_expiry ?? customFields.has_expiry),
    manufacturer: toOptionalString(item.manufacturer ?? customFields.manufacturer),
    brand: toOptionalString(item.brand ?? customFields.brand),
    weight: toOptionalNumber(item.weight ?? customFields.weight),
    supplier_id: toOptionalNumber(item.preferred_supplier_id ?? item.supplier_id ?? customFields.supplier_id),
    supplier_name: toOptionalString(customFields.supplier_name),
    supplier_sku: toOptionalString(item.supplier_sku ?? customFields.supplier_sku),
    custom_fields: customFields,
  };
}

export function mapWorkItemToFinanceItem(
  workItem: WorkItemInventory,
  stockLevelsByItemId: Record<string, WarehouseStockLevel[]> = {}
): FinanceItem {
  const cf = workItem.custom_fields || {};
  
  // Get warehouse stock for this item
  const itemStock = stockLevelsByItemId[workItem.id] || [];
  
  // Aggregate stock across all warehouses
  const totalOnHand = itemStock.reduce((sum, sl) => sum + parseFloat(String(sl.quantity_on_hand || 0)), 0);
  const totalReserved = itemStock.reduce((sum, sl) => sum + parseFloat(String(sl.quantity_reserved || 0)), 0);
  const totalAvailable = itemStock.reduce((sum, sl) => sum + parseFloat(String(sl.quantity_available || 0)), 0);
  
  // Fallback to custom_fields if no warehouse stock data
  const qtyOnHand = itemStock.length > 0 ? totalOnHand : (cf.quantity_on_hand || 0);
  const qtyAllocated = itemStock.length > 0 ? totalReserved : (cf.quantity_allocated || 0);
  const qtyAvailable = itemStock.length > 0 ? totalAvailable : qtyOnHand - qtyAllocated;
  
  // Get display name - DB uses 'title', fallback to 'name' for compatibility
  const displayName = workItem.title || workItem.name || 'Unnamed Item';
  
  // Get friendly item code - prefer item_number (e.g., "PROD-000006"), fallback to sku/barcode
  const itemCode = workItem.item_number || workItem.code || workItem.sku || workItem.barcode || `ITEM-${workItem.id?.substring(0, 8)}`;
  
  return {
    id: workItem.id,
    item_code: itemCode,
    name: displayName,
    description: workItem.description,
    item_type: (workItem.item_subtype === 'service' ? 'service' : workItem.item_subtype === 'bundle' ? 'bundle' : 'product') as 'product' | 'service' | 'bundle',
    contact_id: workItem.contact_id ?? null,
    contact_name: workItem.contact_name ?? null,
    unit_price: cf.selling_price || 0,
    cost_price: cf.cost_price || 0,
    currency: cf.currency || 'ZAR',
    tax_rate_id: cf.tax_rate_id || null,
    tax_rate_name: cf.tax_rate_name || null,
    tax_rate_percentage: cf.tax_rate_percentage ?? undefined,
    is_tax_inclusive: cf.is_tax_inclusive || false,
    track_inventory: workItem.track_inventory ?? cf.track_inventory ?? (workItem.item_subtype === 'product'),
    quantity_on_hand: qtyOnHand,
    quantity_allocated: qtyAllocated,
    quantity_available: qtyAvailable,
    reorder_level: cf.reorder_level || null,
    track_serial_numbers: cf.track_serial_numbers || false,
    track_batches: cf.track_batches || false,
    category_id: workItem.category_id ?? null,
    category_name: workItem.category,
    unit_of_measure: workItem.unit || 'each',
    barcode: workItem.barcode,
    sku: workItem.sku,
    status: workItem.status,
    primary_image_url: workItem.primary_image_url || null,
    // Include warehouse breakdown for detailed view
    warehouse_stock: itemStock.map(sl => ({
      warehouse_id: sl.warehouse_id,
      warehouse_name: sl.warehouse_name,
      quantity_on_hand: parseFloat(String(sl.quantity_on_hand || 0)),
      quantity_available: parseFloat(String(sl.quantity_available || 0))
    }))
  };
}

export interface ItemSelectionOptions {
  quantity: number;
  warehouse_id?: number;
  batch_id?: number;
  serial_numbers?: string[];
  price_override?: number;
  discount_percentage?: number;
  purchase_sourcing?: PurchaseLineSourcingDetails;
  backorder?: {
    backorder_quantity: number;
    expected_date?: string;
  };
}

interface ItemSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: FinanceItem, options: ItemSelectionOptions) => void;
  documentType: 'sales' | 'purchase';
  contactId?: number;
  contactName?: string;
  currency?: string;
  scopeItemsToCustomerContext?: boolean;
  createScopedItemForContact?: boolean;
}

export const ITEM_SEARCH_CACHE_LIMIT = 20;
export const inventorySearchCache = new Map<string, WorkItemInventory[]>();

export function buildInventorySearchCacheKey(params: {
  term: string;
  itemType: string;
  contactId: number | null;
  scopedToContact: boolean;
  itemSubtype?: string;
}) {
  return JSON.stringify({
    term: params.term.trim().toLowerCase(),
    itemType: params.itemType,
    contactId: params.contactId,
    scopedToContact: params.scopedToContact,
    itemSubtype: params.itemSubtype || 'all',
  });
}

export function setInventorySearchCache(key: string, value: WorkItemInventory[]) {
  if (inventorySearchCache.has(key)) {
    inventorySearchCache.delete(key);
  }

  inventorySearchCache.set(key, value);

  while (inventorySearchCache.size > ITEM_SEARCH_CACHE_LIMIT) {
    const oldestKey = inventorySearchCache.keys().next().value;
    if (!oldestKey) {
      break;
    }
    inventorySearchCache.delete(oldestKey);
  }
}

export function clearInventorySearchCache() {
  inventorySearchCache.clear();
}

function indexStockLevelsByItemId(stockLevels: WarehouseStockLevel[]) {
  return stockLevels.reduce<Record<string, WarehouseStockLevel[]>>((acc, stockLevel) => {
    if (!acc[stockLevel.work_item_id]) {
      acc[stockLevel.work_item_id] = [];
    }

    acc[stockLevel.work_item_id].push(stockLevel);
    return acc;
  }, {});
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ItemSearchModal({
  isOpen,
  onClose,
  onSelect,
  documentType,
  contactId,
  contactName,
  currency = 'ZAR',
  scopeItemsToCustomerContext = false,
  createScopedItemForContact = false,
}: ItemSearchModalProps) {
  const { clientBranding, currentTheme } = useTheme();
  const { isMobile: isMobileViewport } = useResponsive();
  const isDarkMode = currentTheme?.mode === 'dark';
  const isMobileModal = isMobileViewport;
  const isNativeMobileShell = isCapacitor();
  const isMobilePanel = isNativeMobileShell || isMobileModal;
  
  // Theme colors
  const primaryColor = clientBranding?.primary_color || '#3B82F6';
  const latestSearchRequestIdRef = useRef(0);
  const latestStockRequestIdRef = useRef(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [inventoryItems, setInventoryItems] = useState<WorkItemInventory[]>([]);
  const [stockLevels, setStockLevels] = useState<WarehouseStockLevel[]>([]);
  // Tracks whether the stock-levels fetch for the current result set has
  // completed. The in_stock_only filter is gated on this so tracked items
  // don't flash "out of stock" (and briefly render an empty list) while
  // warehouse quantities are still in flight.
  const [stockLevelsLoaded, setStockLevelsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [filters, setFilters] = useState({
    item_type: 'all',
    in_stock_only: documentType !== 'purchase',
  });
  const [selectedItemsById, setSelectedItemsById] = useState<Record<string, FinanceItem>>({});
  const [previewItem, setPreviewItem] = useState<InventoryItemData | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalScoped, setCreateModalScoped] = useState(false);
  const [backorderWorkflowOpen, setBackorderWorkflowOpen] = useState(false);
  const [backorderPendingItems, setBackorderPendingItems] = useState<Array<{
    work_item_id: string;
    item_name?: string;
    item_code?: string;
    availability: StockAvailabilityCheck;
  }>>([]);
  const [pendingAddItems, setPendingAddItems] = useState<FinanceItem[]>([]);
  const [pendingAddMode, setPendingAddMode] = useState<'single' | 'bulk' | null>(null);
  // Resolved customer-specific prices keyed by work_item_id, populated by
  // /api/v1/pricing/resolve whenever the visible item set or contactId changes.
  const [resolvedPrices, setResolvedPrices] = useState<Record<string, {
    unit_price: number;
    currency: string;
    source: string;
    price_list_name?: string;
    base_price: number;
  }>>({});
  const [pricesResolving, setPricesResolving] = useState(false);
  const [supplierRelationshipsByItem, setSupplierRelationshipsByItem] = useState<Record<string, SupplierRelationshipSummary[]>>({});
  const normalizedContactId = useMemo(() => {
    if (contactId == null) {
      return null;
    }

    const next = Number(contactId);
    return Number.isFinite(next) && next > 0 ? next : null;
  }, [contactId]);
  const stockLevelsByItemId = useMemo(() => indexStockLevelsByItemId(stockLevels), [stockLevels]);
  const items = useMemo(() => {
    let mappedItems = inventoryItems.map((item) => mapWorkItemToFinanceItem(item, stockLevelsByItemId));

    if (normalizedContactId != null && scopeItemsToCustomerContext) {
      mappedItems = mappedItems.sort((left, right) => {
        const leftScoped = Number(left.contact_id) === normalizedContactId ? 1 : 0;
        const rightScoped = Number(right.contact_id) === normalizedContactId ? 1 : 0;

        if (leftScoped !== rightScoped) {
          return rightScoped - leftScoped;
        }

        return left.name.localeCompare(right.name);
      });
    }

    if (filters.in_stock_only && stockLevelsLoaded) {
      mappedItems = mappedItems.filter((item) => !item.track_inventory || item.quantity_available > 0);
    }

    return mappedItems;
  }, [filters.in_stock_only, inventoryItems, normalizedContactId, scopeItemsToCustomerContext, stockLevelsByItemId, stockLevelsLoaded]);
  const visibleItemIdsKey = useMemo(() => items.map((item) => item.id).join(','), [items]);
  const visibleItemIds = useMemo(
    () => (visibleItemIdsKey ? visibleItemIdsKey.split(',').filter(Boolean) : []),
    [visibleItemIdsKey],
  );

  // Clear the module-level search cache when the modal opens so items created
  // or edited elsewhere (e.g. SharedInventoryWorkspace) are always fresh.
  useEffect(() => {
    if (isOpen) {
      clearInventorySearchCache();
    }
  }, [isOpen]);

  // NOTE: the debounce effect (defined after handleSearch) already runs on mount
  // via the searchTerm/filters dependency. The previous duplicate "load initial
  // items" effect caused two parallel requests on open and was removed to avoid
  // a race.

  // Mobile shared-header visibility tracking is owned by the shared
  // SlideOver chrome (which also dispatches showHeader on open).

  // Fetch stock levels from warehouse system. Pass include_zero=true so items that have
  // a warehouse footprint but currently zero stock still appear (and aren't silently
  // dropped from the picker).
  const fetchStockLevels = useCallback(async (accessToken: string, workItemIds: string[] = []): Promise<WarehouseStockLevel[]> => {
    try {
      const params = new URLSearchParams({
        include_zero: 'true',
        skip_summary: 'true',
        limit: String(Math.max(workItemIds.length, 50)),
      });

      if (workItemIds.length > 0) {
        params.set('work_item_ids', workItemIds.join(','));
      }

      const response = await fetch(`/api/v1/work-items/inventory/stock-levels?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          return result.data;
        }
      }
    } catch (err) {
      console.warn('Failed to fetch stock levels:', err);
    }
    return [];
  }, []);

  const handleSearch = useCallback(async (term: string, options?: { force?: boolean }) => {
    const requestId = ++latestSearchRequestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const authTokens = localStorage.getItem('auth_tokens');
      if (!authTokens) {
        throw new Error('Not authenticated');
      }

      const { access_token } = JSON.parse(authTokens);
      const cacheKey = buildInventorySearchCacheKey({
        term,
        itemType: 'inventory',
        contactId: normalizedContactId,
        scopedToContact: scopeItemsToCustomerContext,
        itemSubtype: filters.item_type,
      });

      if (!options?.force) {
        const cachedItems = inventorySearchCache.get(cacheKey);
        if (cachedItems) {
          if (requestId === latestSearchRequestIdRef.current) {
            setInventoryItems(cachedItems);
            setLoading(false);

            const stockRequestId = ++latestStockRequestIdRef.current;
            setStockLevelsLoaded(false);
            void fetchStockLevels(access_token, cachedItems.map((item) => item.id).filter(Boolean)).then((stockData) => {
              if (stockRequestId !== latestStockRequestIdRef.current) {
                return;
              }

              setStockLevels(stockData);
              setStockLevelsLoaded(true);
            });
          }
          return;
        }
      }

      const fetchInventoryItems = async (params: URLSearchParams): Promise<WorkItemInventory[]> => {
        const response = await fetch(`/api/v1/work-items?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'Search failed');
        }

        return Array.isArray(result.data) ? result.data as WorkItemInventory[] : [];
      };

      const createSearchParams = (requestedContactId?: number | null) => {
        const params = new URLSearchParams({
          item_type: 'inventory',
          page: '1',
          limit: normalizedContactId != null && scopeItemsToCustomerContext ? '200' : '200',
        });

        if (term) {
          params.append('search', term);
        }

        if (filters.item_type !== 'all') {
          params.append('item_subtype', filters.item_type);
        }

        if (requestedContactId != null) {
          params.append('contact_id', String(requestedContactId));
        }

        return params;
      };

      const [genericItems, scopedItems] = await Promise.all([
        fetchInventoryItems(createSearchParams()),
        normalizedContactId != null && scopeItemsToCustomerContext
          ? fetchInventoryItems(createSearchParams(normalizedContactId))
          : Promise.resolve([] as WorkItemInventory[]),
      ]);

      const mergedItems = [...scopedItems, ...genericItems].filter((item) => {
        if (normalizedContactId == null || !scopeItemsToCustomerContext) {
          return true;
        }

        const itemContactId = item.contact_id != null ? Number(item.contact_id) : null;
        return itemContactId == null || itemContactId === normalizedContactId;
      });

      const dedupedItems = mergedItems.reduce<WorkItemInventory[]>((acc, item) => {
        if (acc.some((existing) => existing.id === item.id)) {
          return acc;
        }

        acc.push(item);
        return acc;
      }, []);

      setInventorySearchCache(cacheKey, dedupedItems);

      if (requestId !== latestSearchRequestIdRef.current) {
        return;
      }

      setInventoryItems(dedupedItems);
      // If we got exactly the limit, there may be more results
      setHasMoreResults(genericItems.length >= 200 || scopedItems.length >= 200);

      const stockRequestId = ++latestStockRequestIdRef.current;
      setStockLevelsLoaded(false);
      void fetchStockLevels(access_token, dedupedItems.map((item) => item.id).filter(Boolean)).then((stockData) => {
        if (stockRequestId !== latestStockRequestIdRef.current) {
          return;
        }

        setStockLevels(stockData);
        setStockLevelsLoaded(true);
      });
    } catch (err: unknown) {
      console.error('Item search error:', err);
      if (requestId === latestSearchRequestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to search items');
        setInventoryItems([]);
        setStockLevels([]);
        setStockLevelsLoaded(true);
        setHasMoreResults(false);
      }
    } finally {
      if (requestId === latestSearchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [fetchStockLevels, filters.item_type, normalizedContactId, scopeItemsToCustomerContext]);

  // Debounced search (declared after handleSearch so the dep array can reference it).
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      handleSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.item_type, handleSearch, isOpen, searchTerm]);

  // Batch-resolve prices for the visible items via the centralized resolver.
  // Re-runs whenever the visible set or contactId changes; clears on close.
  useEffect(() => {
    if (!isOpen || visibleItemIds.length === 0) {
      setResolvedPrices({});
      setPricesResolving(false);
      return;
    }

    const tokens = authService.getTokens();
    const accessToken = tokens?.access_token;
    if (!accessToken) {
      setPricesResolving(false);
      return;
    }

    let cancelled = false;
    setPricesResolving(true);
    (async () => {
      try {
        const response = await fetch('/api/v1/pricing/resolve', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            contact_id: contactId ?? null,
            currency,
            list_type: documentType === 'purchase' ? 'buying' : 'selling',
            items: visibleItemIds.map((workItemId) => ({
              work_item_id: workItemId,
              quantity: 1,
            })),
          }),
        });

        if (!response.ok) return;
        const result = await response.json();
        if (cancelled || !result?.success || !Array.isArray(result.data)) return;

        const next: Record<string, {
          unit_price: number;
          currency: string;
          source: string;
          price_list_name?: string;
          base_price: number;
        }> = {};
        for (const row of result.data) {
          if (!row?.work_item_id) continue;
          next[row.work_item_id] = {
            unit_price: Number(row.unit_price ?? 0),
            currency: String(row.currency || currency),
            source: String(row.source || 'item_base'),
            price_list_name: row.source_ref?.price_list_name,
            base_price: Number(row.base_price ?? 0),
          };
        }
        setResolvedPrices(next);
      } catch (err) {
        console.warn('Price resolve failed:', err);
      } finally {
        if (!cancelled) {
          setPricesResolving(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contactId, currency, documentType, isOpen, visibleItemIds]);

  useEffect(() => {
    if (!isOpen || documentType !== 'purchase' || visibleItemIds.length === 0) {
      setSupplierRelationshipsByItem({});
      return;
    }

    const accessToken = authService.getTokens()?.access_token;
    if (!accessToken) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`/api/v1/work-items/inventory/suppliers?work_item_ids=${encodeURIComponent(visibleItemIds.join(','))}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success || !Array.isArray(result.data) || cancelled) {
          return;
        }

        const supplierRows = Array.isArray(result.data)
          ? (result.data as Array<Record<string, unknown>>)
          : [];

        const grouped = supplierRows.reduce<Record<string, SupplierRelationshipSummary[]>>((acc, row) => {
          const key = String(row.work_item_id || '');
          if (!key) {
            return acc;
          }

          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push({
            id: Number(row.id),
            work_item_id: key,
            supplier_contact_id: Number(row.supplier_contact_id),
            supplier_name: typeof row.supplier_name === 'string' ? row.supplier_name : null,
            supplier_sku: typeof row.supplier_sku === 'string' ? row.supplier_sku : null,
            cost_price: row.cost_price == null ? null : Number(row.cost_price),
            currency_code: typeof row.currency_code === 'string' ? row.currency_code : null,
            lead_time_days: row.lead_time_days == null ? null : Number(row.lead_time_days),
            min_order_qty: row.min_order_qty == null ? null : Number(row.min_order_qty),
            is_preferred: Boolean(row.is_preferred),
            priority_rank: row.priority_rank == null ? null : Number(row.priority_rank),
            selection_reason: typeof row.selection_reason === 'string' ? row.selection_reason : null,
            fallback_reason: typeof row.fallback_reason === 'string' ? row.fallback_reason : null,
          });

          return acc;
        }, {});

        setSupplierRelationshipsByItem(grouped);
      } catch (err) {
        console.warn('Supplier relationship fetch failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentType, isOpen, visibleItemIds]);

  useEffect(() => {
    setSelectedItemsById((prev) => {
      let changed = false;
      const next = { ...prev };

      // Update existing selections with fresh item data
      items.forEach((item) => {
        if (next[item.id]) {
          next[item.id] = item;
          changed = true;
        }
      });

      // Remove selections for items no longer visible in the current result set
      const visibleIds = new Set(items.map((item) => item.id));
      for (const id of Object.keys(next)) {
        if (!visibleIds.has(id)) {
          delete next[id];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [items]);

  const selectedItems = useMemo(() => Object.values(selectedItemsById), [selectedItemsById]);
  const selectedItemCount = selectedItems.length;
  const visibleSelectedCount = useMemo(
    () => items.filter((item) => Boolean(selectedItemsById[item.id])).length,
    [items, selectedItemsById],
  );
  const allVisibleSelected = items.length > 0 && visibleSelectedCount === items.length;

  const applyResolvedPrice = useCallback((item: FinanceItem): FinanceItem => {
    if (documentType === 'purchase') {
      const supplierRelationships = supplierRelationshipsByItem[item.id] || [];
      const matchingSupplier = normalizedContactId != null
        ? supplierRelationships.find((relationship) => relationship.supplier_contact_id === normalizedContactId) || null
        : null;
      const preferredSupplier = supplierRelationships.find((relationship) => relationship.is_preferred)
        || supplierRelationships[0]
        || null;
      const effectiveSupplier = matchingSupplier || preferredSupplier;

      if (effectiveSupplier?.cost_price != null) {
        return {
          ...item,
          unit_price: Number(effectiveSupplier.cost_price),
          currency: effectiveSupplier.currency_code || item.currency,
        };
      }

      // No supplier pricing found — fall back to item cost_price for purchases
      // rather than customer-resolved pricing which is for sales documents
      if (item.cost_price > 0) {
        return {
          ...item,
          unit_price: item.cost_price,
        };
      }

      return item;
    }

    const resolved = resolvedPrices[item.id];
    if (!resolved) {
      return item;
    }

    return {
      ...item,
      unit_price: resolved.unit_price,
      currency: resolved.currency || item.currency,
    };
  }, [documentType, normalizedContactId, resolvedPrices, supplierRelationshipsByItem]);

  const getSupplierRelationships = useCallback((itemId: string) => supplierRelationshipsByItem[itemId] || [], [supplierRelationshipsByItem]);

  const getPreferredSupplierRelationship = useCallback((itemId: string) => {
    const relationships = getSupplierRelationships(itemId);
    return relationships.find((relationship) => relationship.is_preferred) || relationships[0] || null;
  }, [getSupplierRelationships]);

  const getSelectedSupplierRelationship = useCallback((itemId: string) => {
    if (normalizedContactId == null) {
      return null;
    }

    return getSupplierRelationships(itemId)
      .find((relationship) => relationship.supplier_contact_id === normalizedContactId) || null;
  }, [getSupplierRelationships, normalizedContactId]);

  const getPurchaseLineSourcingDetails = useCallback((item: FinanceItem): PurchaseLineSourcingDetails | undefined => {
    if (documentType !== 'purchase') {
      return undefined;
    }

    const selectedSupplier = getSelectedSupplierRelationship(item.id);
    const preferredSupplier = getPreferredSupplierRelationship(item.id);
    const effectiveSupplier = selectedSupplier || preferredSupplier;

    if (!effectiveSupplier) {
      return undefined;
    }

    const priceSource: PurchaseLineSourcingDetails['price_source'] = selectedSupplier
      ? (selectedSupplier.is_preferred ? 'selected_preferred_supplier' : 'selected_supplier_mapping')
      : effectiveSupplier.is_preferred
        ? 'preferred_supplier_mapping'
        : 'supplier_mapping';

    return {
      supplier_mapping_id: effectiveSupplier.id,
      supplier_contact_id: effectiveSupplier.supplier_contact_id,
      supplier_name: effectiveSupplier.supplier_name ?? null,
      supplier_sku: effectiveSupplier.supplier_sku ?? null,
      currency_code: effectiveSupplier.currency_code ?? null,
      cost_price: effectiveSupplier.cost_price ?? null,
      lead_time_days: effectiveSupplier.lead_time_days ?? null,
      min_order_qty: effectiveSupplier.min_order_qty ?? null,
      is_preferred: effectiveSupplier.is_preferred,
      priority_rank: effectiveSupplier.priority_rank ?? null,
      selection_reason: effectiveSupplier.selection_reason ?? null,
      fallback_reason: effectiveSupplier.fallback_reason ?? null,
      price_source: priceSource,
      matches_document_contact: selectedSupplier != null,
    };
  }, [documentType, getPreferredSupplierRelationship, getSelectedSupplierRelationship]);

  const toggleItemSelection = useCallback((item: FinanceItem) => {
    setSelectedItemsById((prev) => {
      if (prev[item.id]) {
        const { [item.id]: _removed, ...remaining } = prev;
        return remaining;
      }

      return {
        ...prev,
        [item.id]: item,
      };
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedItemsById((prev) => {
      if (allVisibleSelected) {
        const next = { ...prev };
        items.forEach((item) => {
          delete next[item.id];
        });
        return next;
      }

      const next = { ...prev };
      items.forEach((item) => {
        next[item.id] = item;
      });
      return next;
    });
  }, [allVisibleSelected, items]);

  const clearSelection = useCallback(() => {
    setSelectedItemsById({});
  }, []);

  const handleOpenItemPreview = useCallback(async (item: FinanceItem) => {
    try {
      setPreviewLoadingId(item.id);
      setError(null);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`/api/v1/work-items/${item.id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok || !result?.success || !result.data) {
        throw new Error(result?.error || 'Failed to load item details');
      }

      setPreviewItem(mapWorkItemDetailToInventoryItemData(result.data as WorkItemDetailRecord));
      setPreviewModalOpen(true);
    } catch (err: unknown) {
      console.error('Failed to load item preview:', err);
      setError(err instanceof Error ? err.message : 'Failed to load item preview');
    } finally {
      setPreviewLoadingId(null);
    }
  }, []);

  const handleClose = useCallback(() => {
    setSearchTerm('');
    setInventoryItems([]);
    setError(null);
    setSelectedItemsById({});
    setPreviewItem(null);
    setPreviewModalOpen(false);
    setPreviewLoadingId(null);
    setResolvedPrices({});
    setPricesResolving(false);
    setSupplierRelationshipsByItem({});
    onClose();
  }, [onClose]);

  const addItemToDocument = useCallback((item: FinanceItem, backorderOptions?: ItemSelectionOptions['backorder']) => {
    const purchaseSourcing = getPurchaseLineSourcingDetails(item);
    onSelect(applyResolvedPrice(item), {
      quantity: 1,
      discount_percentage: 0,
      ...(purchaseSourcing ? { purchase_sourcing: purchaseSourcing } : {}),
      ...(backorderOptions ? { backorder: backorderOptions } : {}),
    });
  }, [applyResolvedPrice, getPurchaseLineSourcingDetails, onSelect]);

  const buildStockAvailabilityCheck = useCallback((item: FinanceItem, requestedQty = 1): StockAvailabilityCheck => {
    const availableQty = item.quantity_available ?? 0;
    const shortage = Math.max(0, requestedQty - availableQty);
    const warehouseId = item.warehouse_stock?.[0]?.warehouse_id ?? 0;
    return {
      work_item_id: item.id,
      warehouse_id: warehouseId,
      quantity_on_hand: item.quantity_on_hand ?? 0,
      quantity_reserved: (item.quantity_on_hand ?? 0) - availableQty,
      quantity_available: availableQty,
      quantity_incoming: 0,
      requested_quantity: requestedQty,
      can_fulfill: availableQty >= requestedQty,
      shortage,
      backorder_required: shortage > 0,
    };
  }, []);

  const checkForBackorders = useCallback((itemsToAdd: FinanceItem[]): boolean => {
    const insufficient = itemsToAdd
      .filter(item => item.track_inventory && (item.quantity_available ?? 0) < 1)
      .map(item => ({
        work_item_id: item.id,
        item_name: item.name,
        item_code: item.item_code || undefined,
        // Pass the actual requested quantity (1 at add time; the line
        // quantity is adjusted later in the document editor).
        availability: buildStockAvailabilityCheck(item, 1),
      }));

    if (insufficient.length > 0) {
      setBackorderPendingItems(insufficient);
      setPendingAddItems(itemsToAdd);
      setBackorderWorkflowOpen(true);
      return true;
    }

    return false;
  }, [buildStockAvailabilityCheck]);

  const flushPendingAdds = useCallback((backorderMap: Map<string, ItemSelectionOptions['backorder']>) => {
    if (!pendingAddItems.length) return;

    pendingAddItems.forEach((item) => {
      const bo = backorderMap.get(item.id);
      addItemToDocument(item, bo);
    });

    setPendingAddItems([]);
    setBackorderPendingItems([]);
    setBackorderWorkflowOpen(false);
    handleClose();
  }, [pendingAddItems, addItemToDocument, handleClose]);

  const handleBackorderConfirm = useCallback(async (backorderItems: Array<{
    work_item_id: string;
    item_name?: string;
    item_code?: string;
    requested_quantity: number;
    available_quantity: number;
    shortage: number;
    backorder_quantity: number;
    expected_date?: string;
  }>) => {
    const backorderMap = new Map<string, ItemSelectionOptions['backorder']>();
    backorderItems.forEach((bo) => {
      if (bo.backorder_quantity > 0) {
        backorderMap.set(bo.work_item_id, {
          backorder_quantity: bo.backorder_quantity,
          expected_date: bo.expected_date,
        });
      }
    });
    flushPendingAdds(backorderMap);
  }, [flushPendingAdds]);

  const handleBackorderCancel = useCallback(() => {
    setBackorderWorkflowOpen(false);
    setBackorderPendingItems([]);
    setPendingAddItems([]);
  }, []);

  const handleAddSelectedItems = useCallback(() => {
    if (selectedItems.length === 0) {
      return;
    }

    if (documentType !== 'purchase' && checkForBackorders(selectedItems)) {
      setPendingAddMode('bulk');
      return;
    }

    selectedItems.forEach((item) => addItemToDocument(item));
    handleClose();
  }, [selectedItems, checkForBackorders, addItemToDocument, handleClose, documentType]);

  const handlePreviewModalClose = useCallback(() => {
    setPreviewModalOpen(false);
    setPreviewItem(null);
    setPreviewLoadingId(null);
  }, []);

  const handleSelectAllChange = useCallback(() => {
    toggleAllVisible();
  }, [toggleAllVisible]);

  const handlePreviewCellClick = useCallback((item: FinanceItem) => {
    void handleOpenItemPreview(item);
  }, [handleOpenItemPreview]);

  const handleCardPreviewClick = useCallback((item: FinanceItem) => {
    void handleOpenItemPreview(item);
  }, [handleOpenItemPreview]);

  const handleRowToggle = useCallback((item: FinanceItem) => {
    toggleItemSelection(item);
  }, [toggleItemSelection]);

  const handleRowKeyDown = useCallback((event: React.KeyboardEvent, item: FinanceItem) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggleItemSelection(item);
  }, [toggleItemSelection]);

  const handleRefreshResults = useCallback(() => {
    void handleSearch(searchTerm, { force: true });
  }, [handleSearch, searchTerm]);

  const handleCreateModalClose = useCallback(() => {
    setCreateModalOpen(false);
    setCreateModalScoped(false);
  }, []);

  const handleSaveItem = useCallback(async (itemData: InventoryItemData) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('No authentication token');

      const mergedCustomFields = {
        ...(itemData.custom_fields || {}),
        ...(itemData.sku !== undefined ? { sku: itemData.sku || null } : {}),
        ...(itemData.barcode !== undefined ? { barcode: itemData.barcode || null } : {}),
        ...(itemData.category_id !== undefined ? { category_id: itemData.category_id } : {}),
        ...(itemData.unit_of_measure !== undefined ? { unit_of_measure: itemData.unit_of_measure || null } : {}),
        ...(itemData.weight !== undefined ? { weight: itemData.weight } : {}),
        ...(itemData.cost_price !== undefined ? { cost_price: itemData.cost_price } : {}),
        ...(itemData.selling_price !== undefined ? { selling_price: itemData.selling_price } : {}),
        ...(itemData.currency !== undefined ? { currency: itemData.currency || null } : {}),
        ...(itemData.tax_rate !== undefined ? { tax_rate: itemData.tax_rate } : {}),
        ...(itemData.reorder_level !== undefined ? { reorder_level: itemData.reorder_level } : {}),
        ...(itemData.reorder_quantity !== undefined ? { reorder_quantity: itemData.reorder_quantity } : {}),
        ...(itemData.supplier_id !== undefined ? { supplier_id: itemData.supplier_id } : {}),
        ...(itemData.supplier_sku !== undefined ? { supplier_sku: itemData.supplier_sku || null } : {}),
        ...(itemData.is_serialized !== undefined ? { is_serialized: itemData.is_serialized } : {}),
        ...(itemData.is_batch_tracked !== undefined ? { is_batch_tracked: itemData.is_batch_tracked } : {}),
        ...(itemData.has_expiry !== undefined ? { has_expiry: itemData.has_expiry } : {}),
        ...(itemData.manufacturer !== undefined ? { manufacturer: itemData.manufacturer || null } : {}),
        ...(itemData.brand !== undefined ? { brand: itemData.brand || null } : {}),
      };

      const isEditing = Boolean(itemData.id);
      const payload = {
        ...itemData,
        item_type: 'inventory' as const,
        item_subtype: itemData.item_subtype,
        custom_fields: mergedCustomFields,
        ...(createModalScoped && normalizedContactId != null ? {
          contact_id: normalizedContactId,
          contact_name: contactName || undefined,
        } : {}),
      };

      const endpoint = isEditing ? `/api/v1/work-items/${itemData.id}` : '/api/v1/work-items';
      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const json = await res.json();
        const newItemId = json?.data?.id ?? json?.id;

        // If a customer-specific price was set and we have the new item's UUID,
        // persist it as a customer pricing entry via /api/v1/customer-items.
        const customerEntry = itemData.customer_pricing?.[0];
        if (createModalScoped && newItemId && customerEntry?.contact_id && customerEntry.selling_price != null) {
          try {
            await fetch('/api/v1/customer-items', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                work_item_id: newItemId,
                contact_id: customerEntry.contact_id,
                custom_price: customerEntry.selling_price,
              }),
            });
          } catch (e) {
            console.warn('[ItemSearchModal] customer pricing create failed:', e);
          }
        }

        // Refresh the search results
        void handleSearch(searchTerm, { force: true });
        setCreateModalOpen(false);
        toast.success(isEditing ? 'Item updated successfully' : 'Item created successfully');
      } else {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error || `Failed to ${isEditing ? 'update' : 'create'} item`);
      }
    } catch (err) {
      console.error('Failed to save item:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save item');
      throw err;
    }
  }, [contactName, createModalScoped, handleSearch, normalizedContactId, searchTerm]);

  const handleSavePreviewItem = useCallback(async (itemData: InventoryItemData) => {
    await handleSaveItem(itemData);
    handlePreviewModalClose();
  }, [handleSaveItem, handlePreviewModalClose]);

  const formatCurrency = (amount: number, code: string = currency) => {
    return formatMoney(amount, code);
  };

  // Resolve the price to display: prefer the centralized pricing resolver
  // (customer contract → assigned price list → tier breaks → base) so what
  // the user sees in the picker is exactly what flows into the document.
  const resolveDisplayPrice = (item: FinanceItem): { amount: number; currency: string; source?: string; priceListName?: string } => {
    if (documentType === 'purchase') {
      const selectedSupplier = getSelectedSupplierRelationship(item.id);
      const preferredSupplier = getPreferredSupplierRelationship(item.id);
      const effectiveSupplier = selectedSupplier || preferredSupplier;

      if (effectiveSupplier?.cost_price != null) {
        return {
          amount: Number(effectiveSupplier.cost_price),
          currency: effectiveSupplier.currency_code || item.currency || currency,
          source: selectedSupplier
            ? (selectedSupplier.is_preferred ? 'selected_preferred_supplier' : 'selected_supplier_mapping')
            : effectiveSupplier.is_preferred
              ? 'preferred_supplier_mapping'
              : 'supplier_mapping',
        };
      }
    }

    const resolved = resolvedPrices[item.id];
    if (resolved) {
      return {
        amount: resolved.unit_price,
        currency: resolved.currency || item.currency || currency,
        source: resolved.source,
        priceListName: resolved.price_list_name,
      };
    }
    if (documentType === 'sales') {
      return { amount: item.unit_price, currency: item.currency || currency };
    }
    return { amount: item.cost_price, currency: item.currency || currency };
  };

  const getStockStatus = (item: FinanceItem) => {
    if (!item.track_inventory) return { color: 'gray', text: 'Not tracked' };
    if (item.quantity_available <= 0) return { color: 'red', text: 'Out of stock' };
    if (item.reorder_level && item.quantity_available <= item.reorder_level) {
      return { color: 'orange', text: 'Low stock' };
    }
    // Show warehouse breakdown if multiple warehouses
    const warehouseCount = item.warehouse_stock?.length || 0;
    if (warehouseCount > 1) {
      return {
        color: 'green',
        text: `${formatQuantity(item.quantity_available)} avail (${warehouseCount} locations)`
      };
    }
    return { color: 'green', text: `${formatQuantity(item.quantity_available)} available` };
  };

  const getPurchaseSourcingSummary = useCallback((item: FinanceItem) => {
    if (documentType !== 'purchase') {
      return null;
    }

    const relationships = getSupplierRelationships(item.id);
    if (relationships.length === 0) {
      return null;
    }

    const selectedSupplier = getSelectedSupplierRelationship(item.id);
    const preferredSupplier = getPreferredSupplierRelationship(item.id);
    const effectiveSupplier = selectedSupplier || preferredSupplier;

    if (!effectiveSupplier) {
      return null;
    }

    const reason = selectedSupplier
      ? (selectedSupplier.selection_reason || selectedSupplier.fallback_reason)
      : (effectiveSupplier.selection_reason || effectiveSupplier.fallback_reason);

    return {
      relationships,
      selectedSupplier,
      effectiveSupplier,
      reason,
      alternateCount: Math.max(relationships.length - 1, 0),
    };
  }, [documentType, getPreferredSupplierRelationship, getSelectedSupplierRelationship, getSupplierRelationships]);

  const useCardResults = isMobileModal || viewMode === 'grid';
  const mobileModalTitle = documentType === 'sales' ? 'Add Item' : 'Add Purchase Item';
  const mobileModalSubtitle = documentType === 'sales'
    ? 'Select multiple items, add one instantly, or inspect the full product or service spec'
    : 'Select multiple items, add one instantly, or inspect the full product or service spec';
  const customerScopeLabel = contactName?.trim() || (normalizedContactId != null ? `Customer #${normalizedContactId}` : null);
  const panelTitle = documentType === 'sales' ? 'Insert Items' : 'Insert Purchase Items';
  const resultsSubtitle = selectedItemCount > 0
    ? `${items.length} results · ${selectedItemCount} selected`
    : `${items.length} result${items.length === 1 ? '' : 's'}`;

  if (!isOpen) return null;

  return (
    <>
      <WideSlideOver
        onClose={handleClose}
        icon={Package}
        title={panelTitle}
        accentColor={primaryColor}
        subtitle={<span className="text-[10px] text-gray-500 dark:text-gray-400">{resultsSubtitle}</span>}
        headerActions={
          <>
            <SlideOverHeaderAction
              icon={RefreshCw}
              title="Refresh results"
              onClick={handleRefreshResults}
            />
            <SlideOverHeaderAction
              icon={Plus}
              title="Create general item"
              onClick={() => {
                setCreateModalScoped(false);
                setCreateModalOpen(true);
              }}
            />
          </>
        }
        zIndex={136}
        panelTourId="item-search-modal"
        contentClassName="relative flex min-h-0 flex-1 flex-col overflow-hidden"
      >

          {/* Search & Filters */}
          <div className={`${isMobileModal ? 'px-3 py-3 space-y-2.5' : 'px-6 py-4 space-y-3'} border-b ${
            isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{mobileModalTitle}</p>
              <p className={`mt-0.5 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{mobileModalSubtitle}</p>
            </div>
            {!isMobileModal && (
              <button
                type="button"
                onClick={() => {
                  setCreateModalScoped(true);
                  setCreateModalOpen(true);
                }}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-100'}`}
              >
                <Plus size={16} />
                {createScopedItemForContact && customerScopeLabel ? `Create Item for ${customerScopeLabel}` : 'Add New Item'}
              </button>
            )}
          </div>

          {scopeItemsToCustomerContext && customerScopeLabel && (
            <div className={`rounded-lg border px-3 py-2 text-xs ${
              isDarkMode ? 'border-blue-800 bg-blue-950/30 text-blue-200' : 'border-blue-200 bg-blue-50 text-blue-700'
            }`}>
              Showing shared catalog items plus anything scoped to <span className="font-semibold">{customerScopeLabel}</span>.
            </div>
          )}

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search items..."
              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              autoFocus
            />
          </div>

          {/* Filters Row */}
          <div className={isMobileModal ? 'space-y-2.5' : 'flex items-center justify-between'}>
            <div className={`flex ${isMobileModal ? 'flex-col gap-2.5' : 'items-center gap-3'}`}>
              {/* Type Filter */}
              <select
                value={filters.item_type}
                onChange={(e) => setFilters({ ...filters, item_type: e.target.value })}
                className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobileModal ? 'w-full' : ''} ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="all">All Types</option>
                <option value="product">Products</option>
                <option value="service">Services</option>
              </select>

              {/* Stock Filter */}
              <label className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${isMobileModal ? 'justify-between' : ''} ${
                isDarkMode
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}>
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Show out of stock / back order</span>
                <input
                  type="checkbox"
                  checked={!filters.in_stock_only}
                  onChange={(e) => setFilters({ ...filters, in_stock_only: !e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </label>
            </div>

            <div className={`flex items-center ${isMobileModal ? 'justify-between' : 'gap-2'}`}>
              {/* Results Count */}
              <span className={`text-sm ${isMobileModal ? '' : 'mr-2'} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {items.length} {items.length === 1 ? 'item' : 'items'}
                {pricesResolving && (
                  <span className={`ml-2 text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    resolving prices…
                  </span>
                )}
              </span>
              {selectedItemCount > 0 && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                  {selectedItemCount} selected
                </span>
              )}

              {/* View Toggle */}
              {!isMobileModal && (
              <div className={`flex border rounded-lg overflow-hidden ${
                isDarkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' 
                    ? isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600' 
                    : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <List size={18} />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' 
                    ? isDarkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-50 text-blue-600' 
                    : isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <Grid size={18} />
                </button>
              </div>
              )}
            </div>
          </div>
          </div>

          {/* Results Area */}
          <div
            className={`flex-1 min-h-0 overflow-y-auto ${isMobileModal ? 'px-3 py-3' : 'p-4'} ${isDarkMode ? 'bg-gray-900' : ''}`}
            style={isMobileModal ? { paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' } : undefined}
          >
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
              <span className={`ml-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Searching...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12 text-red-500">
              <AlertCircle size={24} className="mr-2" />
              <span>{error}</span>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && items.length === 0 && (
            <div className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Package size={48} className={`mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className="text-lg font-medium">
                {searchTerm ? `No items found for "${searchTerm}"` : 'No items available yet'}
              </p>
              <p className="text-sm mt-1">Search by name, code, SKU, or barcode</p>
            </div>
          )}

          {/* More results indicator */}
          {!loading && !error && hasMoreResults && items.length > 0 && (
            <div className={`py-2 text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Showing first 200 results — refine your search to find more
            </div>
          )}

          {/* List View */}
          {!loading && !error && items.length > 0 && !useCardResults && (
            <div className={`border rounded-lg overflow-x-auto ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full table-fixed">
                <thead className={`border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <tr>
                    <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase w-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={handleSelectAllChange}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        aria-label={allVisibleSelected ? 'Deselect all visible items' : 'Select all visible items'}
                      />
                    </th>
                    <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase w-[40%] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Item</th>
                    <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Code</th>
                    <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Type</th>
                    <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Price</th>
                    <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase w-24 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Stock</th>
                    <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase w-28 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 bg-gray-800/50' : 'divide-gray-100'}`}>
                  {items.map((item) => {
                    const stock = getStockStatus(item);
                    const isSelected = Boolean(selectedItemsById[item.id]);
                    const purchaseSourcing = getPurchaseSourcingSummary(item);
                    return (
                      <tr
                        key={item.id}
                        tabIndex={0}
                        role="button"
                        aria-pressed={isSelected}
                        onClick={() => handleRowToggle(item)}
                        onKeyDown={(event) => handleRowKeyDown(event, item)}
                        className={`transition-colors ${
                          isSelected
                            ? isDarkMode ? 'bg-blue-900/20 hover:bg-blue-900/30' : 'bg-blue-50 hover:bg-blue-100/80'
                            : isDarkMode ? 'hover:bg-blue-900/20' : 'hover:bg-blue-50'
                        }`}
                      >
                        <td className="px-3 py-2 align-top">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(event) => {
                              event.stopPropagation();
                              toggleItemSelection(item);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            aria-label={`${isSelected ? 'Deselect' : 'Select'} ${item.name}`}
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-start gap-2.5">
                            <InventoryItemThumbnail
                              imageUrl={item.primary_image_url}
                              name={item.name}
                              itemType={item.item_type}
                              isDark={isDarkMode}
                              sizeClassName="h-10 w-10"
                            />
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <div className={`text-[13px] font-medium leading-4 truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.name}</div>
                                {normalizedContactId != null && Number(item.contact_id) === normalizedContactId && (
                                  <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                                    isDarkMode ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-100 text-emerald-700'
                                  }`}>
                                    Customer scoped
                                  </span>
                                )}
                                {previewLoadingId === item.id && (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-600 border-t-transparent" />
                                )}
                              </div>
                              {item.description && (
                                <div className={`truncate text-[11px] leading-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</div>
                              )}
                              {purchaseSourcing && (
                                <div className="mt-0.5 space-y-0.5">
                                  <div className="flex flex-wrap items-center gap-1 text-[10px] leading-4">
                                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 font-semibold ${purchaseSourcing.selectedSupplier
                                      ? (isDarkMode ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-100 text-emerald-700')
                                      : (isDarkMode ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-700')}`}>
                                      {purchaseSourcing.selectedSupplier
                                        ? (purchaseSourcing.selectedSupplier.is_preferred ? 'Selected supplier · preferred' : 'Selected supplier mapped')
                                        : 'Preferred source'}
                                    </span>
                                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      {purchaseSourcing.effectiveSupplier.supplier_name || `Supplier #${purchaseSourcing.effectiveSupplier.supplier_contact_id}`}
                                    </span>
                                    {purchaseSourcing.effectiveSupplier.priority_rank != null && !purchaseSourcing.effectiveSupplier.is_preferred && (
                                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${isDarkMode ? 'bg-purple-900/40 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>
                                        Rank #{purchaseSourcing.effectiveSupplier.priority_rank}
                                      </span>
                                    )}
                                    {purchaseSourcing.effectiveSupplier.lead_time_days != null && (
                                      <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Lead {purchaseSourcing.effectiveSupplier.lead_time_days}d
                                      </span>
                                    )}
                                    {purchaseSourcing.alternateCount > 0 && (
                                      <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        +{purchaseSourcing.alternateCount} backup{purchaseSourcing.alternateCount === 1 ? '' : 's'}
                                      </span>
                                    )}
                                  </div>
                                  {purchaseSourcing.reason && (
                                    <div className={`truncate text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                      {purchaseSourcing.reason}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <span className={`font-mono text-[10px] leading-4 whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.item_code}</span>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <span className={`inline-flex whitespace-nowrap px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            item.item_type === 'product'
                              ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                              : item.item_type === 'bundle'
                                ? isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700'
                                : isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.item_type === 'product' ? (
                              <><Box size={10} className="mr-1" /> Product</>
                            ) : item.item_type === 'bundle' ? (
                              <><Package size={10} className="mr-1" /> Bundle</>
                            ) : (
                              <><Layers size={10} className="mr-1" /> Service</>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          {(() => {
                            const p = resolveDisplayPrice(item);
                            const isCustomerSpecific = p.source === 'customer_contract'
                              || p.source === 'customer_price_list'
                              || p.source === 'customer_price_list_tier';
                            const isSupplierSpecific = p.source === 'selected_preferred_supplier'
                              || p.source === 'selected_supplier_mapping'
                              || p.source === 'preferred_supplier_mapping'
                              || p.source === 'supplier_mapping';
                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className={`font-mono text-sm font-medium leading-4 whitespace-nowrap ${isDarkMode ? 'text-white' : ''}`}>
                                  {formatCurrency(p.amount, p.currency)}
                                </span>
                                {isCustomerSpecific && (
                                  <span
                                    className={`text-[9px] uppercase tracking-wide font-semibold whitespace-nowrap ${
                                      isDarkMode ? 'text-emerald-300' : 'text-emerald-600'
                                    }`}
                                    title={p.priceListName || 'Customer-specific price'}
                                  >
                                    {p.source === 'customer_contract' ? 'Contract' : p.priceListName ? `★ ${p.priceListName}` : '★ Customer'}
                                  </span>
                                )}
                                {isSupplierSpecific && (
                                  <span className={`text-[9px] uppercase tracking-wide font-semibold whitespace-nowrap ${
                                    isDarkMode ? 'text-amber-300' : 'text-amber-600'
                                  }`}>
                                    {p.source === 'selected_preferred_supplier'
                                      ? 'Selected · Preferred'
                                      : p.source === 'selected_supplier_mapping'
                                        ? 'Selected supplier'
                                        : p.source === 'preferred_supplier_mapping'
                                          ? 'Preferred source'
                                          : 'Mapped supplier'}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <span className={`text-[11px] font-medium leading-4 whitespace-nowrap ${
                            stock.color === 'green' ? isDarkMode ? 'text-green-400' : 'text-green-600' :
                            stock.color === 'orange' ? isDarkMode ? 'text-orange-400' : 'text-orange-600' :
                            stock.color === 'red' ? isDarkMode ? 'text-red-400' : 'text-red-600' : 
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {stock.text}
                          </span>
                        </td>
                        <td className="px-3 py-2 align-top text-right">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handlePreviewCellClick(item);
                            }}
                            className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/60 ${
                              isDarkMode
                                ? 'border-gray-600 text-blue-300 hover:bg-gray-700'
                                : 'border-gray-200 text-blue-700 hover:bg-blue-50'
                            }`}
                            aria-label={`View full spec for ${item.name}`}
                          >
                            {previewLoadingId === item.id ? (
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                            ) : (
                              <Eye size={12} />
                            )}
                            {previewLoadingId === item.id ? '...' : 'Spec'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Card Results */}
          {!loading && !error && items.length > 0 && useCardResults && (
            <div className={`grid gap-3 ${isMobileModal ? 'grid-cols-1' : 'grid-cols-3 gap-4'}`}>
              {items.map((item) => {
                const stock = getStockStatus(item);
                const isSelected = Boolean(selectedItemsById[item.id]);
                const purchaseSourcing = getPurchaseSourcingSummary(item);
                return (
                  <div
                    key={item.id}
                    tabIndex={0}
                    role="button"
                    aria-pressed={isSelected}
                    onClick={() => handleRowToggle(item)}
                    onKeyDown={(event) => handleRowKeyDown(event, item)}
                    className={`p-4 border rounded-xl transition-all ${
                      isSelected
                        ? isDarkMode ? 'border-blue-500 bg-blue-900/20 hover:border-blue-400' : 'border-blue-400 bg-blue-50 hover:border-blue-500 hover:shadow-md'
                        : isDarkMode 
                        ? 'border-gray-700 bg-gray-800/50 hover:border-blue-500 hover:bg-gray-800' 
                        : 'border-gray-200 hover:border-blue-500 hover:shadow-md'
                    }`}
                  >
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <label className={`inline-flex items-center gap-2 rounded-lg px-2 py-1 text-xs font-medium ${isDarkMode ? 'bg-gray-900/50 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(event) => {
                            event.stopPropagation();
                            toggleItemSelection(item);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label={`${isSelected ? 'Deselect' : 'Select'} ${item.name}`}
                        />
                        {isSelected ? 'Selected' : 'Select'}
                      </label>
                      <span className={`font-mono text-xs text-right ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{item.item_code}</span>
                    </div>

                    <div className="w-full text-left rounded-lg">
                      <InventoryItemThumbnail
                        imageUrl={item.primary_image_url}
                        name={item.name}
                        itemType={item.item_type}
                        isDark={isDarkMode}
                        sizeClassName="mb-3 h-32 w-full"
                        roundedClassName="rounded-lg"
                        objectFit="cover"
                      />
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            item.item_type === 'product'
                              ? isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                              : item.item_type === 'bundle'
                                ? isDarkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700'
                                : isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.item_type}
                          </span>
                          {normalizedContactId != null && Number(item.contact_id) === normalizedContactId && (
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              isDarkMode ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              Customer scoped
                            </span>
                          )}
                        </div>
                        {previewLoadingId === item.id && (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                        )}
                      </div>
                      
                      <h3 className={`font-medium mb-1 line-clamp-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.name}</h3>
                      
                      {item.description && (
                        <p className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.description}</p>
                      )}

                      {purchaseSourcing && (
                        <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                          isDarkMode ? 'border-gray-700 bg-gray-900/40 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'
                        }`}>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${purchaseSourcing.selectedSupplier
                              ? (isDarkMode ? 'bg-emerald-900/40 text-emerald-200' : 'bg-emerald-100 text-emerald-700')
                              : (isDarkMode ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-700')}`}>
                              {purchaseSourcing.selectedSupplier
                                ? (purchaseSourcing.selectedSupplier.is_preferred ? 'Selected supplier · preferred' : 'Selected supplier mapped')
                                : 'Preferred source'}
                            </span>
                            <span className="font-medium">
                              {purchaseSourcing.effectiveSupplier.supplier_name || `Supplier #${purchaseSourcing.effectiveSupplier.supplier_contact_id}`}
                            </span>
                            {purchaseSourcing.effectiveSupplier.priority_rank != null && !purchaseSourcing.effectiveSupplier.is_preferred && (
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${isDarkMode ? 'bg-purple-900/40 text-purple-200' : 'bg-purple-100 text-purple-700'}`}>
                                Rank #{purchaseSourcing.effectiveSupplier.priority_rank}
                              </span>
                            )}
                            {purchaseSourcing.effectiveSupplier.lead_time_days != null && (
                              <span>Lead {purchaseSourcing.effectiveSupplier.lead_time_days}d</span>
                            )}
                            {purchaseSourcing.alternateCount > 0 && (
                              <span>+{purchaseSourcing.alternateCount} backup{purchaseSourcing.alternateCount === 1 ? '' : 's'}</span>
                            )}
                          </div>
                          {purchaseSourcing.reason && (
                            <p className={`mt-1 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {purchaseSourcing.reason}
                            </p>
                          )}
                        </div>
                      )}

                      <div className={`mb-3 flex flex-wrap items-center gap-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="inline-flex items-center gap-1">
                          {item.item_type === 'product' ? <Box size={12} /> : item.item_type === 'bundle' ? <Package size={12} /> : <Layers size={12} />}
                          {item.item_type === 'product' ? 'Product' : item.item_type === 'bundle' ? 'Bundle' : 'Service'}
                        </span>
                        {item.unit_of_measure && (
                          <span className="inline-flex items-center gap-1">
                            <Package size={12} />
                            {item.unit_of_measure}
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-semibold ${previewLoadingId === item.id
                          ? (isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700')
                          : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
                          <Eye size={12} />
                          {previewLoadingId === item.id ? 'Opening…' : 'Click card to select'}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-between mt-auto pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                        {(() => {
                          const p = resolveDisplayPrice(item);
                          const isCustomerSpecific = p.source === 'customer_contract'
                            || p.source === 'customer_price_list'
                            || p.source === 'customer_price_list_tier';
                          const isSupplierSpecific = p.source === 'selected_preferred_supplier'
                            || p.source === 'selected_supplier_mapping'
                            || p.source === 'preferred_supplier_mapping'
                            || p.source === 'supplier_mapping';
                          return (
                            <div className="flex flex-col">
                              <span className={`text-lg font-semibold font-mono ${isDarkMode ? 'text-white' : ''}`}>
                                {formatCurrency(p.amount, p.currency)}
                              </span>
                              {isCustomerSpecific && (
                                <span
                                  className={`text-[10px] uppercase tracking-wide font-semibold ${
                                    isDarkMode ? 'text-emerald-300' : 'text-emerald-600'
                                  }`}
                                  title={p.priceListName || 'Customer-specific price'}
                                >
                                  {p.source === 'customer_contract' ? 'Contract' : p.priceListName ? `★ ${p.priceListName}` : '★ Customer'}
                                </span>
                              )}
                              {isSupplierSpecific && (
                                <span className={`text-[10px] uppercase tracking-wide font-semibold ${
                                  isDarkMode ? 'text-amber-300' : 'text-amber-600'
                                }`}>
                                  {p.source === 'selected_preferred_supplier'
                                    ? 'Selected · Preferred'
                                    : p.source === 'selected_supplier_mapping'
                                      ? 'Selected supplier'
                                      : p.source === 'preferred_supplier_mapping'
                                        ? 'Preferred source'
                                        : 'Mapped supplier'}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        <span className={`text-xs font-medium ${
                          stock.color === 'green' ? isDarkMode ? 'text-green-400' : 'text-green-600' :
                          stock.color === 'orange' ? isDarkMode ? 'text-orange-400' : 'text-orange-600' :
                          stock.color === 'red' ? isDarkMode ? 'text-red-400' : 'text-red-600' : 
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {stock.text}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCardPreviewClick(item);
                      }}
                      className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/60 ${
                        isDarkMode
                          ? 'border-gray-600 text-blue-300 hover:bg-gray-700'
                          : 'border-gray-200 text-blue-700 hover:bg-blue-50'
                      }`}
                      aria-label={`Open full specification for ${item.name}`}
                    >
                      {previewLoadingId === item.id ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                      ) : (
                        <Eye size={16} />
                      )}
                      {previewLoadingId === item.id ? 'Opening full spec…' : 'View full spec'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          </div>

          {/* Footer */}
          <div
            className={`border-t flex-shrink-0 ${
              isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
            } ${isMobileModal ? 'px-3 py-3' : 'px-6 py-4 flex items-center justify-between'}`}
            style={isMobileModal ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' } : undefined}
          >
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ${isMobileModal ? 'mb-3' : ''}`}>
            {selectedItemCount > 0
              ? `${selectedItemCount} item${selectedItemCount === 1 ? '' : 's'} selected. Add them in one go below, or use Add now on any result for an instant insert.`
              : isMobileModal
                ? 'Select items in bulk, or use Add now for a single-line insert without leaving the picker.'
                : 'Select items in bulk, or use Add now for a single-line insert without leaving the picker.'}
          </div>
          <div className={`flex ${isMobileModal ? 'w-full flex-col gap-2' : 'items-center gap-2 justify-end'}`}>
            {selectedItemCount > 0 && (
              <button
                onClick={clearSelection}
                className={`rounded-lg text-sm ${
                  isDarkMode ? 'text-gray-300 hover:bg-gray-700 border border-gray-600' : 'text-gray-600 hover:bg-gray-200 border border-gray-200'
                } ${isMobileModal ? 'w-full py-3 font-medium' : 'px-4 py-2'}`}
              >
                Clear Selection
              </button>
            )}
            <button
              onClick={handleAddSelectedItems}
              disabled={selectedItemCount === 0}
              className={`rounded-lg text-sm text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isMobileModal ? 'w-full py-3 font-medium' : 'px-4 py-2'
              }`}
              style={{ backgroundColor: selectedItemCount === 0 ? undefined : primaryColor }}
            >
              Add Selected{selectedItemCount > 0 ? ` (${selectedItemCount})` : ''}
            </button>
            <button
              onClick={handleClose}
              className={`rounded-lg text-sm ${
                isDarkMode ? 'text-gray-300 hover:bg-gray-700 border border-gray-600' : 'text-gray-600 hover:bg-gray-200 border border-gray-200'
              } ${isMobileModal ? 'w-full py-3 font-medium' : 'px-4 py-2 border-transparent'}`}
            >
              Cancel
            </button>
          </div>
          </div>
      </WideSlideOver>

      {/* Create Item Modal */}
      {createModalOpen && (
        <InventoryItemModal
          isOpen={createModalOpen}
          onClose={handleCreateModalClose}
          onSave={handleSaveItem}
          defaultSubtype={filters.item_type === 'all' ? 'product' : filters.item_type as InventorySubtype}
          defaultContactId={createModalScoped ? contactId : undefined}
          defaultContactName={createModalScoped ? contactName : undefined}
          placement="right"
          overlayZIndexClassName="z-[160]"
          panelZIndexClassName="z-[161]"
          modalSize="search-match"
        />
      )}

      {previewItem && (
        <InventoryItemModal
          isOpen={previewModalOpen}
          onClose={handlePreviewModalClose}
          onSave={handleSavePreviewItem}
          existingItem={previewItem}
          defaultSubtype={previewItem.item_subtype}
          defaultContactId={contactId}
          defaultContactName={contactName}
          accentColor={primaryColor}
          overlayZIndexClassName="z-[160]"
          panelZIndexClassName="z-[161]"
          modalSize="search-match"
          placement="right"
          readOnly={true}
        />
      )}

      {backorderWorkflowOpen && backorderPendingItems.length > 0 && (
        <BackorderWorkflow
          insufficientItems={backorderPendingItems}
          onConfirm={handleBackorderConfirm}
          onCancel={handleBackorderCancel}
          isDarkMode={isDarkMode}
        />
      )}

    </>
  );
}
